import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ActivityTable } from '../../components/ActivityTable';
import { ScheduleActivity } from '../../types';
import { useGlobalProject } from '../../contexts/GlobalProjectContext';
import { getXERProjectTasks, getAllProjectWBS } from '../../api/schedule';
import MainLayout from '../../components/layout/MainLayout';
import { xerProjectsApi } from '../../api/xerProjects';
import LoadingState from '../../components/ui/LoadingState';
import { Button } from '../../components/ui/button';
import { cn } from '@/lib/utils';

const Activities: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activities, setActivities] = useState<ScheduleActivity[]>([]);
  const [allWBSNodes, setAllWBSNodes] = useState<any[]>([]);
  const [udfLabels, setUdfLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { selectedProject, projectsLoading, projectsError } = useGlobalProject();

  // Helper function to check if an activity is completed
  const isActivityCompleted = (activity: ScheduleActivity): boolean => {
    // Check status field (mapped from backend)
    const status = activity.status || '';
    if (status === 'Completed' || status === 'TK_Complete' || status === 'Complete') {
      return true;
    }
    
    // Check raw status_code if available
    const statusCode = (activity as any).status_code;
    if (statusCode === 'TK_Complete' || statusCode === 'TK_Finish') {
      return true;
    }
    
    // Check percent complete - if 100%, it's completed
    const percentComplete = activity.percent_complete || 
                           activity.effective_percent_complete || 
                           activity.phys_complete_pct || 
                           (activity as any).physical_percent_complete;
    if (percentComplete !== undefined && percentComplete !== null) {
      const percent = typeof percentComplete === 'number' ? percentComplete : parseFloat(String(percentComplete));
      if (!isNaN(percent) && percent >= 100) {
        return true;
      }
    }
    
    return false;
  };

  // Filter activities based on URL query parameters
  const filteredActivities = useMemo(() => {
    let filtered = [...activities];

    // Filter by WBS (canonical: wbs_id or wbs_code)
    const wbsId = searchParams.get('wbs_id');
    if (wbsId != null && wbsId !== '') {
      filtered = filtered.filter(
        (activity) => String((activity as any).wbs_id ?? '') === String(wbsId)
      );
    }
    const wbsCode = searchParams.get('wbs_code');
    if (wbsCode != null && wbsCode !== '' && filtered.length > 0) {
      filtered = filtered.filter((activity) => {
        const shortName = (activity as any).wbs_short_name ?? '';
        const name = activity.wbs_name ?? '';
        return (
          String(shortName) === String(wbsCode) || String(name) === String(wbsCode)
        );
      });
    }

    // Filter by milestones
    const filterMilestones = searchParams.get('filter') === 'milestones';
    if (filterMilestones) {
      filtered = filtered.filter(activity => {
        // Check if it's a milestone using is_milestone field or type field
        return (activity as any).is_milestone === true || 
               activity.type === 'Milestone' ||
               activity.type === 'Finish Milestone' ||
               activity.type === 'Start Milestone';
      });
    }

    // Filter by at-risk (overdue or negative float)
    const atRisk = searchParams.get('atRisk') === 'true';
    if (atRisk) {
      const now = new Date();
      filtered = filtered.filter(activity => {
        // Skip completed activities
        if (isActivityCompleted(activity)) {
          return false;
        }
        
        // Check if overdue (early_end_date < current date and not complete)
        const earlyEnd = activity.early_end_date;
        const isOverdue = earlyEnd && new Date(earlyEnd) < now;
        
        // Check if negative float
        const totalFloatHrs = (activity as any).total_float_hr_cnt;
        const totalFloatDays = totalFloatHrs != null ? totalFloatHrs / 8 : 0;
        const hasNegativeFloat = totalFloatDays < 0;
        
        return isOverdue || hasNegativeFloat;
      });
    }

    // Filter by overdue
    const overdue = searchParams.get('overdue') === 'true';
    if (overdue) {
      const now = new Date();
      filtered = filtered.filter(activity => {
        // Skip completed activities
        if (isActivityCompleted(activity)) {
          return false;
        }
        
        // Check if overdue (early_end_date < current date)
        return activity.early_end_date && new Date(activity.early_end_date) < now;
      });
    }

    // Filter by negative float
    const negativeFloat = searchParams.get('negativeFloat') === 'true';
    if (negativeFloat) {
      filtered = filtered.filter(activity => {
        const totalFloatHrs = (activity as any).total_float_hr_cnt;
        const totalFloatDays = totalFloatHrs != null ? totalFloatHrs / 8 : 0;
        return totalFloatDays < 0;
      });
    }

    // Filter by open ends (activities without predecessors or successors)
    const openEnds = searchParams.get('openEnds') === 'true';
    if (openEnds) {
      // This would require relationship data - for now, we'll skip this filter
      // as it requires additional data that may not be in the activity object
    }

    // Filter by activity_id (single) or activity_ids (comma-separated) — e.g. from Overview "Investigate"
    const activityIdParam = searchParams.get('activity_id');
    const activityIdsParam = searchParams.get('activity_ids');
    const idsToShow: string[] = [];
    if (activityIdParam != null && activityIdParam !== '') {
      idsToShow.push(activityIdParam.trim());
    }
    if (activityIdsParam != null && activityIdsParam !== '') {
      activityIdsParam.split(',').forEach((s) => {
        const id = s.trim();
        if (id && !idsToShow.includes(id)) idsToShow.push(id);
      });
    }
    if (idsToShow.length > 0) {
      filtered = filtered.filter((activity) => {
        const id = String((activity as any).task_id ?? activity.id ?? '');
        return idsToShow.some((tid) => id === tid || id === String(tid));
      });
    }

    // Filter by critical
    const critical = searchParams.get('critical') === 'true';
    if (critical) {
      filtered = filtered.filter(activity => {
        return activity.critical === true ||
               activity.is_critical === true ||
               (activity.total_float !== undefined && activity.total_float === 0) ||
               ((activity as any).total_float_hr_cnt !== undefined && (activity as any).total_float_hr_cnt === 0);
      });
    }

    // Filter by float range (days; total_float_hr_cnt / 8)
    const floatMinParam = searchParams.get('float_min');
    const floatMaxParam = searchParams.get('float_max');
    const floatMin = floatMinParam != null && floatMinParam !== '' ? parseFloat(floatMinParam) : null;
    const floatMax = floatMaxParam != null && floatMaxParam !== '' ? parseFloat(floatMaxParam) : null;
    if (floatMin != null && !Number.isNaN(floatMin)) {
      filtered = filtered.filter(activity => {
        const totalFloatHrs = (activity as any).total_float_hr_cnt;
        const days = totalFloatHrs != null ? totalFloatHrs / 8 : 0;
        return days >= floatMin;
      });
    }
    if (floatMax != null && !Number.isNaN(floatMax)) {
      filtered = filtered.filter(activity => {
        const totalFloatHrs = (activity as any).total_float_hr_cnt;
        const days = totalFloatHrs != null ? totalFloatHrs / 8 : 0;
        return days <= floatMax;
      });
    }

    // Optional sort from URL (drill-through from Analytics)
    const sortParam = searchParams.get('sort');
    if (sortParam === 'total_float_asc') {
      filtered = [...filtered].sort((a, b) => {
        const hrsA = (a as any).total_float_hr_cnt;
        const hrsB = (b as any).total_float_hr_cnt;
        const floatA = hrsA != null ? hrsA / 8 : 0;
        const floatB = hrsB != null ? hrsB / 8 : 0;
        return floatA - floatB;
      });
    }

    return filtered;
  }, [activities, searchParams]);

  const toggleQuickFilter = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || String(value).trim() === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    });
  }, [setSearchParams]);

  const clearAllQuickFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('filter');
      next.delete('atRisk');
      next.delete('overdue');
      next.delete('negativeFloat');
      next.delete('critical');
      next.delete('wbs_id');
      next.delete('wbs_code');
      next.delete('float_min');
      next.delete('float_max');
      next.delete('activity_id');
      next.delete('activity_ids');
      return next;
    });
  }, [setSearchParams]);

  const hasAnyQuickFilter = searchParams.get('filter') === 'milestones' ||
    searchParams.get('atRisk') === 'true' ||
    searchParams.get('overdue') === 'true' ||
    searchParams.get('negativeFloat') === 'true' ||
    searchParams.get('critical') === 'true' ||
    (searchParams.get('wbs_id') != null && searchParams.get('wbs_id') !== '') ||
    (searchParams.get('wbs_code') != null && searchParams.get('wbs_code') !== '') ||
    (searchParams.get('float_min') != null && searchParams.get('float_min') !== '') ||
    (searchParams.get('float_max') != null && searchParams.get('float_max') !== '') ||
    (searchParams.get('activity_id') != null && searchParams.get('activity_id') !== '') ||
    (searchParams.get('activity_ids') != null && searchParams.get('activity_ids') !== '');

  useEffect(() => {
    if (selectedProject) {
      fetchProjectActivities(selectedProject.id);
    } else {
      // Clear activities when no project is selected
      setActivities([]);
      setUdfLabels({});
      setLoading(false);
      setError(null);
    }
  }, [selectedProject]);

  const fetchProjectActivities = async (projectId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      if (import.meta.env.DEV) console.log('🔍 Activities: Fetching activities for project ID:', projectId);
      
      // Fetch activities (now returns {activities, udf_labels})
      const response = await getXERProjectTasks(projectId);
      
      // Handle both old format (array) and new format (object with activities and udf_labels)
      let data: ScheduleActivity[] = [];
      let labels: Record<string, string> = {};
      
      if (Array.isArray(response)) {
        // Old format: just an array
        data = response;
      } else if (response && typeof response === 'object') {
        // New format: object with activities and udf_labels
        data = response.activities || [];
        labels = response.udf_labels || {};
      }
      
      if (import.meta.env.DEV) {
        console.log('🔍 Activities: Activities fetched successfully:', data?.length || 0, 'activities');
        console.log('🔍 Activities: UDF labels:', Object.keys(labels).length, 'labels');
      }
      
      // Fetch ALL WBS nodes to build complete hierarchy (including parents without activities)
      try {
        const allWBSItems = await getAllProjectWBS(projectId);
        if (import.meta.env.DEV) console.log('🔍 Activities: All WBS nodes fetched:', allWBSItems.length, 'WBS nodes');
        setAllWBSNodes(allWBSItems);
        
        // Debug: Log WBS hierarchy info
        if (allWBSItems.length > 0) {
          if (import.meta.env.DEV) console.log('🔍 WBS Hierarchy Debug:', {
            totalWBS: allWBSItems.length,
            withParent: allWBSItems.filter(w => w.parent_wbs_id).length,
            withoutParent: allWBSItems.filter(w => !w.parent_wbs_id).length,
            uniqueWBSIds: new Set(allWBSItems.map(w => String(w.wbs_id))).size,
            sampleWBS: allWBSItems.slice(0, 5).map(w => ({
              wbs_id: w.wbs_id,
              wbs_name: w.wbs_name,
              parent_wbs_id: w.parent_wbs_id
            }))
          });
        }
      } catch (wbsErr) {
        if (import.meta.env.DEV) console.warn('⚠️ Activities: Could not fetch all WBS nodes, trying rollup API:', wbsErr);
        // Fallback to rollup API if new endpoint fails
        try {
          const numericProjectId = Number(projectId);
          if (!isNaN(numericProjectId)) {
            const wbsResponse = await xerProjectsApi.getWBSRollup(numericProjectId, 1, 10000);
            setAllWBSNodes(wbsResponse.items || []);
          }
        } catch (fallbackErr) {
          if (import.meta.env.DEV) console.warn('⚠️ Activities: Could not fetch WBS nodes from rollup API either:', fallbackErr);
          // Continue without WBS nodes - will use activity-based grouping
        }
      }
      
      setActivities(data || []);
      setUdfLabels(labels);
    } catch (err) {
      if (import.meta.env.DEV) console.error('❌ Activities: Error fetching activities:', err);
      setError(err instanceof Error ? err.message : 'Failed to load project activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title="Activities">
      <div className="flex flex-col flex-1 min-h-0 p-4 lg:p-6">
        {/* Error Display for Project Data */}
        {error && (
          <div className="mb-4 bg-destructive/10 border border-destructive/30 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-destructive" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-destructive">Error Loading Project Activities</h3>
                <div className="mt-1 text-sm text-destructive/80">{error}</div>
                {selectedProject && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchProjectActivities(selectedProject.id)}
                    className="mt-2"
                  >
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error Display for Projects List */}
        {projectsError && (
          <div className="mb-4 bg-destructive/10 border border-destructive/30 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-destructive" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-destructive">Error Loading Projects</h3>
                <div className="mt-1 text-sm text-destructive/80">{projectsError}</div>
                <p className="mt-1 text-xs text-muted-foreground">Use the retry button in the top navigation to reload projects.</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State - Project Data */}
        {loading && (
          <LoadingState 
            message={`Fetching activities for ${selectedProject?.name}...`}
            size="lg"
          />
        )}

        {/* Loading State - Projects List */}
        {projectsLoading && !selectedProject && (
          <LoadingState 
            message="Fetching available projects..."
            size="lg"
          />
        )}

        {/* Quick filters (URL params) */}
        {!loading && selectedProject && activities.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mr-1">Filters</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleQuickFilter('filter', searchParams.get('filter') === 'milestones' ? '' : 'milestones')}
              className={cn('h-7 px-2.5 text-xs rounded-md', searchParams.get('filter') === 'milestones' && 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15')}
            >
              Milestones
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleQuickFilter('atRisk', searchParams.get('atRisk') === 'true' ? '' : 'true')}
              className={cn('h-7 px-2.5 text-xs rounded-md', searchParams.get('atRisk') === 'true' && 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15')}
            >
              At Risk
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleQuickFilter('overdue', searchParams.get('overdue') === 'true' ? '' : 'true')}
              className={cn('h-7 px-2.5 text-xs rounded-md', searchParams.get('overdue') === 'true' && 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15')}
            >
              Overdue
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleQuickFilter('negativeFloat', searchParams.get('negativeFloat') === 'true' ? '' : 'true')}
              className={cn('h-7 px-2.5 text-xs rounded-md', searchParams.get('negativeFloat') === 'true' && 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15')}
            >
              Neg. Float
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleQuickFilter('critical', searchParams.get('critical') === 'true' ? '' : 'true')}
              className={cn('h-7 px-2.5 text-xs rounded-md', searchParams.get('critical') === 'true' && 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15')}
            >
              Critical
            </Button>
            {hasAnyQuickFilter && (
              <Button variant="ghost" size="sm" onClick={clearAllQuickFilters} className="h-7 px-2 text-xs text-muted-foreground ml-1">
                Clear
              </Button>
            )}
          </div>
        )}

        {/* Activities Table - single scroll: table section gets bounded height */}
        {!loading && selectedProject && activities.length > 0 && (
          <div className="flex flex-col flex-1 min-h-0 min-w-0">
            <ActivityTable
              activities={filteredActivities}
              allWBSNodes={allWBSNodes}
              loading={loading}
              udfLabels={udfLabels}
              title="Activities"
              toolbarSubtitle={selectedProject.name}
              scrollPersistKey={selectedProject && !hasAnyQuickFilter ? `activities.${selectedProject.id}` : undefined}
            />
          </div>
        )}

        {/* Empty States */}
        {!loading && !projectsLoading && !selectedProject && !projectsError && (
          <div className="text-center py-16">
            <div className="bg-card border border-border rounded-lg p-8 max-w-md mx-auto">
              <div className="p-4 bg-muted rounded-lg w-fit mx-auto mb-4">
                <svg className="h-10 w-10 text-muted-foreground mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-display font-semibold text-foreground mb-2">Select a Project</h3>
              <p className="text-sm text-muted-foreground">
                Choose a project from the dropdown in the top navigation to view its activities.
              </p>
            </div>
          </div>
        )}

        {!loading && selectedProject && filteredActivities.length === 0 && activities.length > 0 && (
          <div className="text-center py-16">
            <div className="bg-card border border-border rounded-lg p-8 max-w-md mx-auto">
              <div className="p-4 bg-muted rounded-lg w-fit mx-auto mb-4">
                <svg className="h-10 w-10 text-muted-foreground mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-display font-semibold text-foreground mb-2">No Activities Match Filter</h3>
              <p className="text-sm text-muted-foreground mb-4">
                No activities match the current filter criteria. Try adjusting your filters.
              </p>
              <Button variant="outline" size="sm" onClick={clearAllQuickFilters}>
                Clear filters
              </Button>
            </div>
          </div>
        )}

        {!loading && selectedProject && activities.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="bg-card border border-border rounded-lg p-8 max-w-md mx-auto">
              <div className="p-4 bg-muted rounded-lg w-fit mx-auto mb-4">
                <svg className="h-10 w-10 text-muted-foreground mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-display font-semibold text-foreground mb-2">No Activities Found</h3>
              <p className="text-sm text-muted-foreground">
                This project has no activities to display.
              </p>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Activities;
